import React from 'react';
import cx from 'classnames';
import styles from '../../styles/Button.module.scss';

export interface Props {
  /** Class name to inject into component. */
  className?: string;
  // button element will get this style prop
  style?: any;

  /** Custon button color */
  color?: 'blue' | 'green' | 'gray'
  /** Label within button */
  label?: string;
  /** Button input name */
  name?: string;
  /** Click handler */
  onClick?: (e: React.SyntheticEvent) => any;
  /** Display outline button instead of solid button */
  outline?: boolean;
  /** Button type
   * @default button
   */
  type?: 'button' | 'submit' | 'reset';
  // a disabled button is grayed out
  disabled?: boolean;
  // pass in an icon that will be display in the button
  // NOTE: icon size & color are controlled by you, the button component doesn't
  // handle those automatically
  icon?: any;
  // position of the passed in icon compared to the label
  iconPosition?: 'left' | 'right'
  // set 'round' to true if you want the button to be round 
  // NOTE1: a round button won't have any text, but you can pass in a prop instead
  // NOTE2: the iconPosition prop doens't matter for a round button, the icon will be centered
  round?: boolean;
}

const Button = ({
  className,
  style,
  color = 'blue',
  label,
  name,
  onClick,
  outline,
  type = 'button',
  disabled,
  icon,
  iconPosition = 'left',
  round,
}: Props) => {
  function getButtonContent() {
    // if no icon or label, show nothing
    if (!icon && !label) return null;

    // a round button will only ever possibly show an icon
    if (round && !icon) return null;
    if (round && icon) return (
      <div className={styles.content}>
        {icon}
      </div> 
    )

    // button with either a label or icon but not both will show whichever we have
    if (label && !icon) return (
      <div className={styles.content}>
        <p style={{ margin: 0 }}>{label}</p>
      </div>
    )
    if (icon && !label) return (
      <div className={styles.content}>
        {icon}
      </div>
    )

    // button with both icon and label uses iconPosition to determine where to put the icon
    if (iconPosition === 'left') return (
      <div className={styles.content}>
        {icon}
        <p style={{ margin: '0 0 0 8px' }}>{label}</p>
      </div>
    )
    if (iconPosition === 'right') return (
      <div className={styles.content}>
        <p style={{ margin: '0 8px 0 0' }}>{label}</p>
        {icon}
      </div>
    )

    return null;
  }

  return (
    <button
      className={cx(styles.btn, className, styles[color], {
        [styles.disabled]: disabled,
        [styles.outline]: outline,
        [styles.round]: round
      })}
      name={name}
      onClick={onClick}
      type={type}
      style={{ ...style }}
    >
      {getButtonContent()}
    </button>
  )
}

export default Button;
